import { useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, CheckCircleIcon } from '@components/icons';
import type { Student } from '@skjv/common';
import { readingLevelToDisplayText, writingLevelToDisplayText } from '@skjv/common';

interface ImportGroupDialogProps {
  students: Student[];
  onConfirm: (groupName: string) => void;
  onCancel: () => void;
}

/**
 * Dialog shown during import when a study has no group.
 * Displays inferred students and asks for a group name.
 * Portaled to document.body: modals are application-modal, and a panel
 * ancestor's backdrop-filter would otherwise contain the fixed overlay.
 */
export function ImportGroupDialog({ students, onConfirm, onCancel }: ImportGroupDialogProps) {
  const [groupName, setGroupName] = useState('');

  const handleConfirm = () => {
    const name = groupName.trim();
    if (name) onConfirm(name);
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            Name This Study Group
          </h2>
          <button
            onClick={onCancel}
            className="p-1.5 text-stone-400 transition-colors hover:text-stone-600 dark:hover:text-stone-300"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-sm text-stone-600 dark:text-stone-400">
          This study doesn't include group information. Name the group to complete the import.
        </p>

        <input
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
          placeholder="e.g. Sunday School, Youth Group..."
          autoFocus
          className="mb-4 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-900 dark:text-stone-200 dark:placeholder-stone-500"
        />

        {students.length > 0 && (
          <div className="mb-4 rounded-lg border border-stone-200 bg-stone-50 p-3 dark:border-stone-700 dark:bg-stone-900/50">
            <span className="mb-2 block text-xs font-medium text-stone-500 dark:text-stone-400">
              Students ({students.length})
            </span>
            <ul className="space-y-1">
              {students.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between text-sm text-stone-700 dark:text-stone-300"
                >
                  <span>{s.name}</span>
                  <span className="text-xs text-stone-400 dark:text-stone-500">
                    {readingLevelToDisplayText(s.readingLevel)} /{' '}
                    {writingLevelToDisplayText(s.writingLevel)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-stone-600 transition-colors hover:bg-stone-100 dark:border-stone-600 dark:text-stone-400 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!groupName.trim()}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-white transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Import
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
